<script setup lang="ts">
import BaseAsset from './BaseAsset.vue';
import {
  PortfolioAsset,
  getAddressFromAsset,
  getImageFromAsset,
  getLabelFromAsset,
  networks,
  collectibleFreezedTag,
} from '@sonarwatch/portfolio-core';
import { computed } from 'vue';
import { useDataStore } from '@/store/data';
import { TagDetails } from '@/types';
import { capitalizeFirstLetter } from '@/helpers/capitalizeFirstLetter';
const store = useDataStore();
const props = defineProps<{
  asset: PortfolioAsset;
  hideLabel?: boolean;
  showNetwork?: boolean;
}>();

const address = computed(() => getAddressFromAsset(props.asset));
const tokenInfo = computed(() =>
  store.getTokenInfo(address.value, props.asset.networkId),
);
const image = computed(() => getImageFromAsset(props.asset, tokenInfo.value));
const label = computed(() => getLabelFromAsset(props.asset, tokenInfo.value));
const networkImage = computed(() =>
  props.showNetwork ? networks[props.asset.networkId].image : undefined,
);

// Tags
let tags: TagDetails[] | undefined = [];
if (
  props.asset.attributes.lockedUntil !== undefined &&
  props.asset.attributes.lockedUntil > Date.now()
) {
  tags.push({
    text: new Date(props.asset.attributes.lockedUntil).toLocaleDateString(
      undefined,
      {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      },
    ),
    icon: 'mdi-lock',
  });
} else if (
  props.asset.attributes.lockedUntil !== undefined &&
  props.asset.attributes.lockedUntil === -1
) {
  tags.push({
    text: 'Locked',
    icon: 'mdi-lock',
  });
} else if (props.asset.attributes.isClaimable) {
  tags.push({
    text: 'Claimable',
    icon: 'mdi-lock-open',
  });
} else if (
  props.asset.attributes.lockedUntil &&
  props.asset.attributes.lockedUntil <= Date.now()
) {
  tags.push({
    text: 'Unlocked',
    icon: 'mdi-lock-open',
  });
}

// Asset attributes tags
if (props.asset.attributes.tags) {
  const assetTags = props.asset.attributes.tags.filter(
    (t) => ![collectibleFreezedTag].includes(t),
  );
  tags.push(
    ...assetTags.map(
      (t): TagDetails => ({
        text: capitalizeFirstLetter(t),
        icon: undefined,
      }),
    ),
  );
}

if (tags.length === 0) tags = undefined;
</script>

<template>
  <BaseAsset
    :text-to-copy="address || undefined"
    :images="[image]"
    :hide-label="hideLabel"
    :label="label"
    :badge-image="networkImage"
    :tags="tags"
  />
</template>
